import React from "react";
import styles from "./MovieOverlay.module.scss";
import { ISectionItem } from "../../_types/interfaces";
import { genresData } from "../../../../constants/structure";
import { movieTitleHandler, posterPathHandler } from "../../../../utils/helpers";
import { useTranslation } from "react-i18next";
import OverlayRatingIcon from "../../../../components/icons/searchIcons/OverlayRatingIcon";
import OverlayTomatoRatingIcon from "../../../../components/icons/searchIcons/OverlayTomatoRatingIcon";

type GenreItem = (typeof genresData)[number];

const formatRating = (rating?: number | null) => (rating != null ? rating.toFixed(1) : undefined);

interface MovieOverlayProps {
  item: ISectionItem;
  visible?: boolean;
}

const MovieOverlay = ({ item, visible = true }: MovieOverlayProps) => {
  const { t, i18n } = useTranslation();

  if (item.commercial_url) return null;

  const title = movieTitleHandler(i18n.language, item);
  const posterPath = posterPathHandler(i18n.language, item);
  const year = item.release_date?.substring(0, 4);
  const genres = (item.genre_ids as number[])
    .slice(0, 2)
    .map((genreId) => genresData.find((g) => g.id === genreId))
    .filter((genre): genre is GenreItem => Boolean(genre));
  const primaryRating = formatRating(item.imdb_rating ?? item.vote_average);
  const secondaryRating = formatRating(item.rotten_tomatoes_rating ?? item.imdb_rating ?? item.vote_average);

  return (
    <div data-movie-overlay className={`${styles.overlay} ${visible ? styles.visible : styles.hidden}`}>
      {posterPath && (
        <img
          className={styles.poster}
          src={`${process.env.REACT_APP_IMG_BASE_URL}${posterPath}`}
          alt={title}
          draggable={false}
        />
      )}
      <div className={styles.content}>
        <div className={styles.titleWrapper}>
          <div className={styles.title}>{title}</div>
          <span className={styles.titleTooltip}>{title}</span>
        </div>
        <div className={styles.meta}>
          {genres.map((genre) => (
            <span key={genre?.id} className={styles.genre}>
              {t(genre?.name || "")}
            </span>
          ))}
          {year && <span className={styles.year}>{year}</span>}
        </div>
        {primaryRating && (
          <div className={styles.ratingRow}>
            <span className={styles.rating}>
              <OverlayRatingIcon />
              {primaryRating}
            </span>
            <span className={styles.rating}>
              <OverlayTomatoRatingIcon />
              {secondaryRating}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default MovieOverlay;
